/**
 * Component: Product Kit
 * ------------------------------------------------------------------------------
 * A script to load the related product kit items on the product page.
 *
 * @namespace productKit
 */
import axios from 'axios'

import {imageParameters} from '../helpers/utils'

import log from 'salvo-lite/log'

class ProductKit {
    constructor (theme, elem) {
        this._theme = theme
        this._elem = elem
        this._settings = {
            productKitSKU: '[js-product-kit-string]',
            productKitContainer: '[js-product-kit-container]',
            productKitCard: '[js-product-kit-card]',
            ...JSON.parse(elem.getAttribute('data-product-kit') || '{}')
        }

        /**
         * Node selectors.
         */
        this._nodeSelectors = {
            productKitSKU: document.querySelector(this._settings.productKitSKU),
            productKitContainer: document.querySelectorAll(this._settings.productKitContainer)
        }

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        if (this._nodeSelectors.productKitContainer.length) {
            this._loadProductKit()
        }
    }

    /**
     * Load product kit if SKU data exists
     */
    _loadProductKit () {
        const sku = this._nodeSelectors.productKitSKU.dataset.sku

        if (sku) {
            this._retrieveProductKit(sku)
                .then((response) => {
                    if (response) {
                        [...this._nodeSelectors.productKitContainer].forEach((container) => {
                            container.innerHTML = response
                        })

                        return
                    }

                    throw new Error('Response not found')
                })
                .catch((error) => error)
        }
    }

    /**
     * Retrieve the product kit items based on search query using SKU search.
     * @param {string} - url query string
     * @returns {string} combined string of all returned products
     */
    _retrieveProductKit (sku) {
        return new Promise((resolve, reject) => {
            axios.get(`/search?type=product&q=${sku}&view=sku`)
                .then((response) => {
                    if (response) {
                        resolve(response.data.products.map((product) => {
                            return this._getProductKitCardTemplate(product)
                        }).join(''))

                        /**
                 * Event picked up by installation to update parts data.
                 */
                        window.Frame.EventBus.emit('ProductKit:loaded', response.data.products)

                        return
                    }

                    throw new Error('No response found!')
                })
                .catch((error) => reject(error))
        })
    }

    /**
     * Get the HTML markup for the product kit items.
     * @param {string} - product data retrieved from axios response
     * @returns {string} Product HTML
     */
    _getProductKitCardTemplate (product) {
        return `
        <div class="product-kit__card product-kit-card" js-product-kit-card>
            ${this._getProductKitImage(product)}

            <div class="product-kit-card__meta">
            <h3 class="h6">
                <a class="product-kit-card__title" href="/products/${product.handle}">${product.title}</a>
            </h3>
            <div class="product-kit-card__sku">${window.theme.strings.partNumber} ${product.variants[0].sku}</div>
            </div>
        </div>
        `
    }

    /**
     * Get the HTML markup for the product kit image
     * @param {string} product product data retrieved from axios response
     * @returns {string} Product Image HTML if product has an image.
     */
    _getProductKitImage (product) {
        if (product.featured_image) {
            return `
            <div class="product-kit-card__thumbnail-container">
            <a href="/products/${product.handle}">
                <img class="product-kit-card__thumbnail ${(product.type === 'Tyre') ? 'product-kit-card__thumbnail--tyre' : ''}" src="${imageParameters(product.featured_image, {size: '360x360', crop: 'center'})}" alt=""
                srcset="
                    ${imageParameters(product.featured_image, {size: '900x900', crop: 'center'})} 900w,
                    ${imageParameters(product.featured_image, {size: '720x720', crop: 'center'})} 720w,
                    ${imageParameters(product.featured_image, {size: '540x540', crop: 'center'})} 540w,
                    ${imageParameters(product.featured_image, {size: '360x360', crop: 'center'})} 360w,
                    ${imageParameters(product.featured_image, {size: '180x180', crop: 'center'})} 180w"
                sizes="auto"
                >
            </a>
            </div>
        `
        }

        return ''
    }

    /**
     * Expose public interface.
     */
    // return Object.freeze({
    //     init,
    // })
}

export default ProductKit



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ProductKit.js